import {IModel} from 'makerjs';
import Length from "../../../source/js/shared/models/Geometry/Length";
import DrillHole from "./DrillHole";
import ShapeDrillHoleGeneratorFactory from "./ShapeDrillHoleGeneratorFactory";
import isEmpty from "lodash/isEmpty";
import OffsetLine from './Lines/OffsetLine';
import { renderLength } from '@/functions/lengths';

export enum EdgeDrillHoleType {
	CORNERS = 'corners',
	AROUND = 'around',
}

export default class EdgeDrillHoles {
	shape: string;
	primitives: {
		width: number;
		length: number;
	};
	diameter: Length;
	offset: Length;
	type: string;
	drillHoles: number[];
	private parentModel: MakerJs.IModel;

	constructor( shape: string, parentModel: IModel, diameter: Length, offset: Length, type: EdgeDrillHoleType, drillHoles?: number[] ) {
		this.shape = shape;
		this.diameter = diameter;
		this.offset = offset;
		this.type = type;
		this.drillHoles = drillHoles;
		this.parentModel = parentModel;
	}

	getId(): string {
		return `edge-drillHoles-${this.type}`;
	}

	getPath(): DrillHole[] {
		const shapeDrillHoleGeneratorFactory = new ShapeDrillHoleGeneratorFactory();
		const shapeDrillHoleGenerator = shapeDrillHoleGeneratorFactory.getShapeDrillHoleGenerator( this.shape, this.parentModel );

		let drillHoles = [];

		if ( this.type === EdgeDrillHoleType.CORNERS && 'getCorners' in shapeDrillHoleGenerator ) {
			drillHoles = shapeDrillHoleGenerator.getCorners( this.offset, this.diameter );
		}

		if ( ( this.type === EdgeDrillHoleType.AROUND && 'getAround' in shapeDrillHoleGenerator ) && ! isEmpty( this.drillHoles ) ) {
			drillHoles = shapeDrillHoleGenerator.getAround( this.drillHoles, this.offset, this.diameter );
		}

		return drillHoles;
	}

	getAsModel(): IModel {
		const drillHoles = this.getPath();
		const drillHolesModel = { paths: {}, type: 'edge-drillHole' };

		drillHoles.forEach( ( drillHole ) => {
			drillHolesModel.paths = Object.assign( drillHolesModel.paths, drillHole.getAsModel().paths );
		} );

		return drillHolesModel;
	}

	// Search for de closest drill hole to the [0,0] point
	findClosestDrillHole(): DrillHole | null {
		const drillHoles = this.getPath();
		if (drillHoles.length === 0) return null;

		let closestDrillHole = drillHoles[0];
		let minDistance = Math.sqrt(Math.pow(closestDrillHole.x.in_mm(), 2) + Math.pow(closestDrillHole.y.in_mm(), 2));

		drillHoles.forEach(drillHole => {
			const distance = Math.sqrt(Math.pow(drillHole.x.in_mm(), 2) + Math.pow(drillHole.y.in_mm(), 2));
			if (distance < minDistance) {
				closestDrillHole = drillHole;
				minDistance = distance;
			}
		});

		return closestDrillHole;
	}

	getOffsetLines(focusedElement = ''): OffsetLine[] {
		if(focusedElement.includes('drillHoleOffset')) {
			const drillHole = this.findClosestDrillHole();
			if(!drillHole) {
				return [];
			}

			return [
				new OffsetLine(
					[ drillHole.x.in_mm(), 0 ],
					[ drillHole.x.in_mm(), drillHole.y.in_mm() ],
					renderLength(this.offset.in_cm())
				),
			];
		}

		return [];
	}

	getCurrentEdgeDrillHoles(focusedElement = ''): IModel {
		const drillHoleModel = this.getAsModel();

		if(this.type !== EdgeDrillHoleType.AROUND && focusedElement.startsWith('drillHoleEdgeCount')) {
			return;
		}

		const edge = +focusedElement.split('_')[1];

		const filteredDrillHoles = Object.keys(drillHoleModel.paths)
			.filter(key => key.endsWith(`-${edge + 1}`))
			.reduce((obj, key) => {
				obj[key] = drillHoleModel.paths[key];
				return obj;
			}, {});

		drillHoleModel.paths = filteredDrillHoles;

		return drillHoleModel;
	}
}
